
import React, { useState } from 'react';
import { useData } from '../../context/DataContext';
import { Truck } from '../../types';
import { Plus, Edit, Trash2, X, Check, Upload, Image as ImageIcon, ArrowUp, ArrowDown, ListOrdered } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const InventoryManager: React.FC = () => {
  const { trucks, addTruck, updateTruck, deleteTruck, settings } = useData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReorderModalOpen, setIsReorderModalOpen] = useState(false);
  const [isInventoryReorderModalOpen, setIsInventoryReorderModalOpen] = useState(false);
  const [reorderList, setReorderList] = useState<Truck[]>([]);
  const [inventoryReorderList, setInventoryReorderList] = useState<Truck[]>([]);
  const [editingTruck, setEditingTruck] = useState<Partial<Truck>>({});
  const [uploading, setUploading] = useState(false);

  const openModal = (truck?: Truck) => {
    if (truck) {
      setEditingTruck({ ...truck });
    } else {
      setEditingTruck({
        id: Math.random().toString(36).substr(2, 9),
        name: '',
        brand: '',
        category: settings.vehicleCategories[0] || 'Heavy Duty',
        year: new Date().getFullYear(),
        engine: '',
        horsepower: 0,
        image: '',
        maxSpeed: '',
        available: true,
        featured: false,
        location: 'Dar es Salaam',
        features: [],
        description: '',
        transmission: 'Manual',
        fuelType: 'Diesel',
        condition: 'Used',
        color: '',
        gallery: []
      });
    }
    setIsModalOpen(true);
  };

  const openReorderModal = () => {
    // Get only featured trucks and sort them by current order
    const featured = trucks
      .filter(t => t.featured)
      .sort((a, b) => (a.featuredOrder || 0) - (b.featuredOrder || 0));
    setReorderList(featured);
    setIsReorderModalOpen(true);
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    const newList = [...reorderList];
    if (direction === 'up' && index > 0) {
      [newList[index], newList[index - 1]] = [newList[index - 1], newList[index]];
    } else if (direction === 'down' && index < newList.length - 1) {
      [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
    }
    setReorderList(newList);
  };

  const saveReorder = async () => {
    try {
      // Update each truck with its new index
      const updates = reorderList.map((truck, index) => {
        return updateTruck({ ...truck, featuredOrder: index });
      });
      await Promise.all(updates);
      setIsReorderModalOpen(false);
    } catch (error) {
      console.error("Error saving order:", error);
      alert("Failed to save new order.");
    }
  };

  const openInventoryReorderModal = () => {
    // Get all trucks and sort by current order
    const allTrucks = [...trucks].sort((a, b) => (a.order || 0) - (b.order || 0));
    setInventoryReorderList(allTrucks);
    setIsInventoryReorderModalOpen(true);
  };

  const moveInventoryItem = (index: number, direction: 'up' | 'down') => {
    const newList = [...inventoryReorderList];
    if (direction === 'up' && index > 0) {
      [newList[index], newList[index - 1]] = [newList[index - 1], newList[index]];
    } else if (direction === 'down' && index < newList.length - 1) {
      [newList[index], newList[index + 1]] = [newList[index + 1], newList[index]];
    }
    setInventoryReorderList(newList);
  };

  const saveInventoryReorder = async () => {
    try {
      const updates = inventoryReorderList.map((truck, index) => {
        return updateTruck({ ...truck, order: index });
      });
      await Promise.all(updates);
      setIsInventoryReorderModalOpen(false);
    } catch (error) {
      console.error("Error saving inventory order:", error);
      alert("Failed to save new inventory order.");
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingTruck.id && editingTruck.name) {
      const truckData = editingTruck as Truck;
      // Provide defaults if new fields are missing (for backward compatibility during edit)
      truckData.description = truckData.description || '';
      truckData.transmission = truckData.transmission || 'Manual';
      truckData.fuelType = truckData.fuelType || 'Diesel';
      truckData.condition = truckData.condition || 'Used';
      truckData.color = truckData.color || 'White';
      truckData.gallery = truckData.gallery || [];
      truckData.image = truckData.image || 'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&q=80&w=1000'; // Default fallback

      const exists = trucks.some(t => t.id === editingTruck.id);
      try {
        if (exists) {
          updateTruck(truckData);
        } else {
          addTruck(truckData);
        }
        setIsModalOpen(false);
      } catch (error: any) {
        console.error("Error saving truck:", error);
        alert(`Error saving vehicle: ${error.message || error}`);
      }
    }
  };

  const handleChange = (field: keyof Truck, value: any) => {
    setEditingTruck(prev => ({ ...prev, [field]: value }));
  };

  const handleFeaturesChange = (value: string) => {
    const features = value.split(',').map(f => f.trim());
    setEditingTruck(prev => ({ ...prev, features }));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size before processing (max 5MB original)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image file is too large. Please choose an image under 5MB.');
        return;
      }

      setUploading(true);

      // 1. Try Cloudinary Upload if configured
      if (settings.cloudinaryCloudName && settings.cloudinaryUploadPreset) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('upload_preset', settings.cloudinaryUploadPreset);
          const res = await fetch(`https://api.cloudinary.com/v1_1/${settings.cloudinaryCloudName}/image/upload`, {
            method: 'POST',
            body: formData
          });
          const data = await res.json();
          if (data.secure_url) {
            handleChange('image', data.secure_url);
            setUploading(false);
            return;
          }
        } catch (error) {
          console.error("Cloud upload failed, falling back to local", error);
        }
      }

      // 2. Fallback to Local Compression (FileReader)
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const MAX_SIZE = 600; // Reduced from 800 to ensure smaller file size

          if (width > height) {
            if (width > MAX_SIZE) {
              height *= MAX_SIZE / width;
              width = MAX_SIZE;
            }
          } else {
            if (height > MAX_SIZE) {
              width *= MAX_SIZE / height;
              height = MAX_SIZE;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
            // Increased compression: 0.5 quality (from 0.7) to reduce size
            const dataUrl = canvas.toDataURL('image/jpeg', 0.5);

            // Check if resulting base64 is too large (>500KB)
            const sizeInBytes = (dataUrl.length * 3) / 4;
            if (sizeInBytes > 500 * 1024) {
              alert('Compressed image is still too large. Please use a smaller or simpler image.');
              setUploading(false);
              return;
            }

            handleChange('image', dataUrl);
            setUploading(false);
          }
        };
        img.src = event.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGalleryUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      // Limit to 3 gallery images max
      const currentGalleryCount = editingTruck.gallery?.length || 0;
      if (currentGalleryCount + files.length > 3) {
        alert('Maximum 3 gallery images allowed. Please remove some existing images first.');
        return;
      }

      setUploading(true);
      const newImages: string[] = [];

      // Process each file
      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Check file size before processing (max 5MB original)
        if (file.size > 5 * 1024 * 1024) {
          alert(`Image ${i + 1} is too large. Skipping.`);
          continue;
        }

        // 1. Try Cloudinary
        if (settings.cloudinaryCloudName && settings.cloudinaryUploadPreset) {
          try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('upload_preset', settings.cloudinaryUploadPreset);
            const res = await fetch(`https://api.cloudinary.com/v1_1/${settings.cloudinaryCloudName}/image/upload`, {
              method: 'POST',
              body: formData
            });
            const data = await res.json();
            if (data.secure_url) {
              newImages.push(data.secure_url);
              continue;
            }
          } catch (error) {
            console.error("Gallery upload failed for one image", error);
          }
        }

        // 2. Fallback to Local
        await new Promise<void>((resolve) => {
          const reader = new FileReader();
          reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
              const canvas = document.createElement('canvas');
              let width = img.width;
              let height = img.height;
              const MAX_SIZE = 600; // Match main image size

              if (width > height) {
                if (width > MAX_SIZE) {
                  height *= MAX_SIZE / width;
                  width = MAX_SIZE;
                }
              } else {
                if (height > MAX_SIZE) {
                  width *= MAX_SIZE / height;
                  height = MAX_SIZE;
                }
              }
              canvas.width = width;
              canvas.height = height;
              const ctx = canvas.getContext('2d');
              if (ctx) {
                ctx.drawImage(img, 0, 0, width, height);
                const dataUrl = canvas.toDataURL('image/jpeg', 0.5); // Match main image quality

                // Check size
                const sizeInBytes = (dataUrl.length * 3) / 4;
                if (sizeInBytes > 500 * 1024) {
                  alert(`Gallery image ${i + 1} is too large after compression. Skipping.`);
                } else {
                  newImages.push(dataUrl);
                }
              }
              resolve();
            };
            img.src = event.target?.result as string;
          };
          reader.readAsDataURL(file);
        });
      }

      setEditingTruck(prev => ({
        ...prev,
        gallery: [...(prev.gallery || []), ...newImages]
      }));
      setUploading(false);
    }
  };

  const removeGalleryImage = (index: number) => {
    setEditingTruck(prev => {
      const newGallery = [...(prev.gallery || [])];
      newGallery.splice(index, 1);
      return { ...prev, gallery: newGallery };
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Inventory Management</h1>
          <p className="text-gray-500">Add, edit, or remove trucks from the website.</p>
        </div>
        <div className="flex gap-4">
          <button
            onClick={openReorderModal}
            className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-3 rounded-sm font-bold flex items-center space-x-2 transition-colors"
          >
            <ListOrdered size={20} />
            <ListOrdered size={20} />
            <span>Reorder Featured</span>
          </button>
          <button
            onClick={openInventoryReorderModal}
            className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-3 rounded-sm font-bold flex items-center space-x-2 transition-colors"
          >
            <ListOrdered size={20} />
            <span>Reorder Inventory</span>
          </button>
          <button
            onClick={() => openModal()}
            className="bg-automax-red hover:bg-red-700 text-white px-6 py-3 rounded-sm font-bold flex items-center space-x-2 transition-colors"
          >
            <Plus size={20} />
            <span>Add New Truck</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-sm shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Vehicle</th>
                <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Price</th>
                <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Specs</th>
                <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {trucks.map(truck => (
                <tr key={truck.id} className="hover:bg-gray-50">
                  <td className="p-4">
                    <div className="flex items-center">
                      <img src={truck.image} alt={truck.name} className="w-12 h-12 rounded-sm object-cover mr-4" />
                      <div>
                        <div className="font-bold text-gray-900">{truck.name}</div>
                        <div className="text-xs text-gray-500">{truck.year} • {truck.brand}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 font-mono font-medium">--</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 text-xs font-bold uppercase rounded-sm ${truck.available ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {truck.available ? 'Available' : 'Sold Out'}
                    </span>
                  </td>
                  <td className="p-4 text-sm text-gray-600">
                    <div className="text-xs">{truck.transmission || 'Manual'}</div>
                    <div className="text-xs text-gray-400">{truck.fuelType || 'Diesel'}</div>
                  </td>
                  <td className="p-4 text-right space-x-2">
                    <button onClick={() => openModal(truck)} className="text-gray-400 hover:text-blue-600 transition-colors">
                      <Edit size={18} />
                    </button>
                    <button onClick={() => { if (window.confirm('Are you sure?')) deleteTruck(truck.id) }} className="text-gray-400 hover:text-red-600 transition-colors">
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h2 className="text-xl font-bold text-gray-900">{editingTruck.id && trucks.some(t => t.id === editingTruck.id) ? 'Edit Truck' : 'Add New Truck'}</h2>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-900"><X size={24} /></button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-8">

              {/* Left Column: Image & Status */}
              <div className="lg:col-span-1 space-y-6">
                <div className="space-y-2">
                  <label className="block text-sm font-bold text-gray-700">Vehicle Image</label>
                  <div className="border-2 border-dashed border-gray-300 rounded-sm p-4 text-center hover:bg-gray-50 transition-colors relative">
                    {editingTruck.image ? (
                      <div className="relative group">
                        <img src={editingTruck.image} alt="Preview" className="w-full h-48 object-cover rounded-sm" />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <span className="text-white font-bold text-sm">Change Image</span>
                        </div>
                      </div>
                    ) : (
                      <div className="py-12 flex flex-col items-center text-gray-400">
                        <ImageIcon size={32} className="mb-2 opacity-50" />
                        <span className="text-sm">{uploading ? 'Uploading...' : 'No Image Selected'}</span>
                      </div>
                    )}

                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploading}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                    />
                  </div>
                </div>
                <div className="text-xs text-gray-500 text-center">Tap to upload from device</div>

                {/* Gallery Section */}
                <div className="space-y-2">
                  <label className="block text-sm font-bold text-gray-700">Gallery Images</label>
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    {editingTruck.gallery?.map((img, idx) => (
                      <div key={idx} className="relative group aspect-square bg-gray-100 rounded-sm overflow-hidden border border-gray-200">
                        <img src={img} alt={`Gallery ${idx}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeGalleryImage(idx)}
                          className="absolute top-1 right-1 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                    <div className="aspect-square border-2 border-dashed border-gray-300 rounded-sm flex flex-col items-center justify-center text-gray-400 hover:bg-gray-50 hover:border-automax-red transition-colors relative">
                      <Upload size={20} />
                      <span className="text-xs mt-1">Add</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleGalleryUpload}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                    </div>
                  </div>
                  <div className="text-xs text-gray-500">Add multiple images to showcase details.</div>
                </div>

                <div className="bg-gray-50 p-4 rounded-sm border border-gray-200">
                  <div className="flex items-center space-x-2 mb-4">
                    <input type="checkbox" id="available" checked={editingTruck.available} onChange={e => handleChange('available', e.target.checked)} className="w-5 h-5 text-automax-red" />
                    <label htmlFor="available" className="text-sm font-bold text-gray-700">Mark as Available</label>
                  </div>

                  <div className="flex items-center space-x-2 mb-4">
                    <input type="checkbox" id="featured" checked={editingTruck.featured || false} onChange={e => handleChange('featured', e.target.checked)} className="w-5 h-5 text-automax-red" />
                    <label htmlFor="featured" className="text-sm font-bold text-gray-700">Featured on Homepage</label>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Condition</label>
                    <select value={editingTruck.condition} onChange={e => handleChange('condition', e.target.value)} className="w-full border p-2 rounded-sm bg-white">
                      <option value="Used">Used</option>
                      <option value="New">New</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Center & Right Columns: Form Data */}
              <div className="lg:col-span-2 space-y-6">

                <div className="space-y-4">
                  <h3 className="font-bold text-gray-900 border-b pb-2">Basic Information</h3>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Name / Model Title</label>
                    <input required type="text" value={editingTruck.name} onChange={e => handleChange('name', e.target.value)} className="w-full border p-2 rounded-sm focus:border-automax-red outline-none" placeholder="e.g. Scania R500 V8" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Brand</label>
                      <input required type="text" value={editingTruck.brand} onChange={e => handleChange('brand', e.target.value)} className="w-full border p-2 rounded-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Year</label>
                      <input required type="number" value={editingTruck.year} onChange={e => handleChange('year', Number(e.target.value))} className="w-full border p-2 rounded-sm" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Description</label>
                    <div className="bg-white">
                      <ReactQuill
                        theme="snow"
                        value={editingTruck.description}
                        onChange={(value) => handleChange('description', value)}
                        modules={{
                          toolbar: [
                            [{ 'header': [1, 2, 3, false] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                            ['link', 'clean'],
                            [{ 'color': [] }, { 'background': [] }],
                            ['blockquote', 'code-block']
                          ],
                        }}
                        className="h-64 mb-12"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-bold text-gray-900 border-b pb-2">Technical Specifications</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Category</label>
                      <select value={editingTruck.category} onChange={e => handleChange('category', e.target.value)} className="w-full border p-2 rounded-sm bg-white">
                        {settings.vehicleCategories.map((cat: string) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {!editingTruck.category?.toLowerCase().includes('trailer') && (
                      <>
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-1">Transmission</label>
                          <select value={editingTruck.transmission} onChange={e => handleChange('transmission', e.target.value)} className="w-full border p-2 rounded-sm bg-white">
                            <option value="Manual">Manual</option>
                            <option value="Automatic">Automatic</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-1">Fuel Type</label>
                          <select value={editingTruck.fuelType} onChange={e => handleChange('fuelType', e.target.value)} className="w-full border p-2 rounded-sm bg-white">
                            <option value="Diesel">Diesel</option>
                            <option value="Petrol">Petrol</option>
                            <option value="Electric">Electric</option>
                          </select>
                        </div>
                      </>
                    )}
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Exterior Color</label>
                      <input type="text" value={editingTruck.color} onChange={e => handleChange('color', e.target.value)} className="w-full border p-2 rounded-sm" placeholder="e.g. White" />
                    </div>
                  </div>

                  {!editingTruck.category?.toLowerCase().includes('trailer') && (
                    <>
                      <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-1">Max Speed</label>
                          <div className="relative">
                            <input
                              list="maxSpeedOptions"
                              type="text"
                              value={editingTruck.maxSpeed}
                              onChange={e => handleChange('maxSpeed', e.target.value)}
                              className="w-full border p-2 rounded-sm"
                              placeholder="e.g. 90 km/h"
                            />
                            <datalist id="maxSpeedOptions">
                              {settings.vehicleMaxSpeeds?.map((speed: string) => (
                                <option key={speed} value={speed} />
                              ))}
                            </datalist>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-1">Engine Type</label>
                          <input required type="text" value={editingTruck.engine} onChange={e => handleChange('engine', e.target.value)} className="w-full border p-2 rounded-sm" />
                        </div>
                        <div>
                          <label className="block text-sm font-bold text-gray-700 mb-1">Horsepower</label>
                          <input required type="number" value={editingTruck.horsepower} onChange={e => handleChange('horsepower', Number(e.target.value))} className="w-full border p-2 rounded-sm" />
                        </div>
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Location</label>
                    <input required type="text" value={editingTruck.location} onChange={e => handleChange('location', e.target.value)} className="w-full border p-2 rounded-sm" />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-1">Features (comma separated)</label>
                    <textarea rows={2} value={editingTruck.features?.join(', ') || ''} onChange={e => handleFeaturesChange(e.target.value)} className="w-full border p-2 rounded-sm"></textarea>
                  </div>
                </div>

                <div className="flex justify-end gap-4 pt-4 border-t border-gray-100">
                  <button type="button" onClick={() => setIsModalOpen(false)} className="px-6 py-3 border border-gray-300 text-gray-700 font-bold rounded-sm hover:bg-gray-50 transition-colors">Cancel</button>
                  <button disabled={uploading} type="submit" className={`px-6 py-3 bg-automax-red text-white font-bold rounded-sm hover:bg-red-700 transition-colors flex items-center gap-2 ${uploading ? 'opacity-50' : ''}`}>
                    <Check size={18} /> {uploading ? 'Uploading...' : 'Save Truck'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div >
      )}
      {/* Reorder Modal */}
      {isReorderModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">Reorder Featured Vehicles</h2>
              <button onClick={() => setIsReorderModalOpen(false)} className="text-gray-400 hover:text-gray-900"><X size={24} /></button>
            </div>

            <div className="p-6">
              <p className="text-gray-500 mb-4 text-sm">Drag and drop functionality not available silently. Please use the up/down arrows to reorder.</p>
              <div className="space-y-2">
                {reorderList.map((truck, index) => (
                  <div key={truck.id} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-sm">
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-gray-400 w-6">{index + 1}.</span>
                      <img src={truck.image} alt={truck.name} className="w-10 h-10 object-cover rounded-sm" />
                      <span className="font-bold text-gray-800">{truck.name}</span>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => moveItem(index, 'up')}
                        disabled={index === 0}
                        className="p-2 hover:bg-gray-200 rounded text-gray-600 disabled:opacity-30"
                      >
                        <ArrowUp size={16} />
                      </button>
                      <button
                        onClick={() => moveItem(index, 'down')}
                        disabled={index === reorderList.length - 1}
                        className="p-2 hover:bg-gray-200 rounded text-gray-600 disabled:opacity-30"
                      >
                        <ArrowDown size={16} />
                      </button>
                    </div>
                  </div>
                ))}
                {reorderList.length === 0 && <p className="text-center text-gray-500 py-8">No featured vehicles found.</p>}
              </div>

              <div className="flex justify-end gap-4 mt-6 pt-4 border-t border-gray-100">
                <button onClick={() => setIsReorderModalOpen(false)} className="px-6 py-3 border border-gray-300 text-gray-700 font-bold rounded-sm hover:bg-gray-50 transition-colors">Cancel</button>
                <button onClick={saveReorder} className="px-6 py-3 bg-automax-red text-white font-bold rounded-sm hover:bg-red-700 transition-colors">
                  Save Order
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Inventory Reorder Modal */}
      {
        isInventoryReorderModalOpen && (
          <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4">
            <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-6 border-b border-gray-100">
                <h2 className="text-xl font-bold text-gray-900">Reorder All Inventory</h2>
                <button onClick={() => setIsInventoryReorderModalOpen(false)} className="text-gray-400 hover:text-gray-900"><X size={24} /></button>
              </div>

              <div className="p-6">
                <p className="text-gray-500 mb-4 text-sm">Use the up/down arrows to arrange vehicles. This order will be reflected on the Inventory page.</p>
                <div className="space-y-2">
                  {inventoryReorderList.map((truck, index) => (
                    <div key={truck.id} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-sm">
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-gray-400 w-6">{index + 1}.</span>
                        <img src={truck.image} alt={truck.name} className="w-10 h-10 object-cover rounded-sm" />
                        <div>
                          <span className="font-bold text-gray-800 block">{truck.name}</span>
                          <span className="text-xs text-gray-500">{truck.category}</span>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => moveInventoryItem(index, 'up')}
                          disabled={index === 0}
                          className="p-2 hover:bg-gray-200 rounded text-gray-600 disabled:opacity-30"
                        >
                          <ArrowUp size={16} />
                        </button>
                        <button
                          onClick={() => moveInventoryItem(index, 'down')}
                          disabled={index === inventoryReorderList.length - 1}
                          className="p-2 hover:bg-gray-200 rounded text-gray-600 disabled:opacity-30"
                        >
                          <ArrowDown size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                  {inventoryReorderList.length === 0 && <p className="text-center text-gray-500 py-8">No vehicles found.</p>}
                </div>

                <div className="flex justify-end gap-4 mt-6 pt-4 border-t border-gray-100">
                  <button onClick={() => setIsInventoryReorderModalOpen(false)} className="px-6 py-3 border border-gray-300 text-gray-700 font-bold rounded-sm hover:bg-gray-50 transition-colors">Cancel</button>
                  <button onClick={saveInventoryReorder} className="px-6 py-3 bg-automax-red text-white font-bold rounded-sm hover:bg-red-700 transition-colors">
                    Save Inventory Order
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default InventoryManager;
